import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import _isEmpty from 'lodash/isEmpty';
import { Tooltip } from 'react-tooltip';

import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import MarketStateExportService from '@app/model/market_state/_export/service/MarketStateExportService';
import ExportService from '@app/model/market_state/_export/service/ExportService';
import TablePlaceholder from '@app/component/placeholder/TablePlaceholder';
import Message from '@app/component/message/Message';
import MarketStateExportReportExecution from '@app/model/market_state/_export/MarketStateExportReportExecution';
import MarketStateExportReport, { MarketStateExportReportPeriod } from '@app/model/market_state/_export/MarketStateExportReport';
import { ReportExecutionState } from '@app/model/market_state/_export/ReportExecution';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import useDialogue from '@app/component/overlays/dialog/DialogueRef';
import Dialogue from '@app/component/overlays/dialog/Dialogue';
import FiltersCell from '@app/site/secured/rateshopper/rates/market-state-export/FiltersCell';
import Utilities from '@util/Utilities';
import moment from 'moment';

export class MarketStateExportUtilities {
	static formatDateRange = (fromDate: string | null, toDate: string | null): string => {
		if (!fromDate || !toDate) return '-';

		return `${Utilities.formatDateString(fromDate, 'DD/MM/yyyy', false)} - ${Utilities.formatDateString(toDate, 'DD/MM/yyyy', false)}`;
	};

	static formatReportPeriod = (report: MarketStateExportReport): string => {
		if (report.period !== MarketStateExportReportPeriod.CUSTOM_DATES) {
			return MarketStateExportUtilities.formatPeriodLabel(report.period);
		}

		return MarketStateExportUtilities.formatCustomPeriod(report.fromDate, report.toDate) ?? 'Custom dates';
	};

	static formatPeriodLabel = (period: MarketStateExportReportPeriod): string => {
		switch (period) {
			case MarketStateExportReportPeriod.CURRENT_MONTH:
				return 'Current month';
			case MarketStateExportReportPeriod.MONTHS_3:
				return 'Next 3 months';
			case MarketStateExportReportPeriod.MONTHS_6:
				return 'Next 6 months';
			case MarketStateExportReportPeriod.MONTHS_12:
				return 'Next 12 months';
			case MarketStateExportReportPeriod.CUSTOM_DATES:
			default:
				return 'Custom dates';
		}
	};

	static formatCustomPeriod = (fromDate: string | null, toDate: string | null): string | undefined => {
		if (!fromDate || !toDate) return undefined;

		const from = moment(fromDate, 'YYYY-MM-DD', true).startOf('day');
		const to = moment(toDate, 'YYYY-MM-DD', true).startOf('day');

		if (!from.isValid() || !to.isValid() || to.isBefore(from)) return undefined;

		const fullCalendarMonths = from.date() === 1 && to.isSame(to.clone().endOf('month'), 'day');
		if (fullCalendarMonths) {
			const months = to.diff(from, 'months') + 1;
			return months === 1 ? 'Current month' : `${months} months`;
		}

		const days = to.diff(from, 'days') + 1;
		if (days % 7 === 0) {
			const weeks = days / 7;
			return weeks === 1 ? 'Weekly' : `${weeks} weeks`;
		}

		return days === 1 ? '1 day' : `${days} days`;
	};

	static formatStatus = (state: ReportExecutionState): string => {
		return state
			.split('_')
			.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join(' ');
	};

	static handleDownload = (exp: MarketStateExportReportExecution) => {
		ExportService.downloadFile(exp, exp.execution.filename ?? `${exp.report.name}.${exp.execution.effectiveFormat === 'CSV' ? 'zip' : 'xlsx'}`);
	};
}


const FileNameCell: React.FC<{
	exportExecution: MarketStateExportReportExecution;
}> = ({ exportExecution }) => {
	const fileName = exportExecution.execution.filename ?? exportExecution.report.name;
	const tooltipId = `market-state-export-filename-${exportExecution.execution.id}`;

	return (
		<td className="px-2 py-3 text-sm">
			<span
				data-tooltip-id={tooltipId}
				data-tooltip-content={fileName}
				className="block max-w-[280px] truncate cursor-help"
			>
				{fileName}
			</span>
			<Tooltip id={tooltipId} className="max-w-xl break-words" />
		</td>
	);
};

const statusClassName = (state: ReportExecutionState): string => {
	switch (state) {
		case ReportExecutionState.SUCCESS:
			return 'bg-green-50 text-green-700';
		case ReportExecutionState.FAILED:
			return 'bg-red-50 text-red-700';
		case ReportExecutionState.IN_PROCESS:
			return 'bg-blue-50 text-blue-700';
		case ReportExecutionState.NEW:
		default:
			return 'bg-amber-50 text-amber-700';
	}
};

const canDelete = (state: ReportExecutionState): boolean => {
	return state !== ReportExecutionState.IN_PROCESS;
};

const canDownload = (state: ReportExecutionState): boolean => {
	return state === ReportExecutionState.SUCCESS;
};

const MarketStateExportPage: React.FC = () => {
	const { companyHotel } = useSelectedCompanyHotel()!;
	const queryClient = useQueryClient();
	const toaster = useToaster();
	const deleteDialogue = useDialogue<number>();
	const queryKey = ['market-state-export-report-executions', companyHotel.id];

	const { data: exportExecutions, isLoading } = useQuery({
		queryKey,
		queryFn: () => MarketStateExportService.findCompanyHotelMarketStateExportReportExecutions(companyHotel.id),
	});

	const deleteMutation = useMutation({
		mutationFn: (reportExecutionId: number) => MarketStateExportService.deleteMarketStateExportReportExecution(reportExecutionId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey });
			toaster.success('Downloads', 'Export successfully deleted.');
		},
		onError: () => {
			toaster.error('Downloads', 'Failed to delete export. Please try again.');
		},
	});

	if (isLoading) {
		return (
			<Wrapper>
				<TablePlaceholder />
			</Wrapper>
		);
	}

	const threeMonthsAgo = new Date();
	threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

	const sortedExportExecutions = [...(exportExecutions ?? [])]
		.filter(exp => new Date(exp.execution.creationDate).getTime() >= threeMonthsAgo.getTime())
		.sort((a, b) => new Date(b.execution.creationDate).getTime() - new Date(a.execution.creationDate).getTime());

	if (_isEmpty(sortedExportExecutions)) {
		return (
			<Wrapper>
				<Message state="warning">There are no exports available!</Message>
			</Wrapper>
		);
	}

	return (
		<Wrapper>
			<div className="bg-white p-6 sm:p-10 rounded-xl">
				<div className="sm:flex sm:items-center">
					<div className="sm:flex-auto">
						<h1 className="text-base font-semibold leading-6 text-gray-900">Downloads</h1>
						<p className="text-sm leading-6 text-gray-500">
							Below are your on-demand market state export executions from the past three months.
						</p>
					</div>
				</div>
				<div className="mt-8 flow-root">
					<div className="overflow-x-auto">
						<div className="inline-block min-w-full align-middle">
							<table className="min-w-full divide-y divide-gray-300 text-gray-500">
								<thead>
								<tr>
									<th className="whitespace-nowrap py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
										Created
									</th>
									<th className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900">
										Selected Dates
									</th>
									<th className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900">
										Type
									</th>
									<th className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900">
										File
									</th>
									<th className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900">
										Selected Filters
									</th>
									<th className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900">
										Status
									</th>
									<th className="relative whitespace-nowrap py-3.5 pl-3 pr-4 sm:pr-0">
										<span className="sr-only">Actions</span>
									</th>
								</tr>
								</thead>
								<tbody className="divide-y divide-gray-200 bg-white">
								{sortedExportExecutions.map((exp) => (
									<tr key={exp.execution.id}>
										<td className="whitespace-nowrap py-3 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0">
											{Utilities.formatDateString(exp.execution.creationDate, 'DD/MM/yyyy')}{' '}
											<span className="text-gray-500 text-xs">
												{Utilities.formatDateString(exp.execution.creationDate, 'HH:mm')}
											</span>
										</td>
										<td className="whitespace-nowrap px-2 py-3 text-sm">
											{MarketStateExportUtilities.formatDateRange(exp.report.fromDate, exp.report.toDate)}
										</td>
										<td className="whitespace-nowrap px-2 py-3 text-sm">
											{MarketStateExportUtilities.formatReportPeriod(exp.report)}
										</td>
										<FileNameCell exportExecution={exp} />
										<FiltersCell competitorTags={companyHotel.competitorTags} filter={exp.report.filter} exportId={exp.execution.id} />
										<td className="whitespace-nowrap px-2 py-3 text-sm">
											<span className={`inline-flex items-center rounded-lg px-2 py-1 text-xs font-medium ${statusClassName(exp.execution.state)}`}>
												{MarketStateExportUtilities.formatStatus(exp.execution.state)}
											</span>
											{exp.execution.reason && <div className="mt-1 max-w-xs truncate text-xs text-gray-500">{exp.execution.reason}</div>}
										</td>
										<td className="relative whitespace-nowrap py-3 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
											<div className="flex items-center justify-end space-x-3">
												{canDelete(exp.execution.state) && (
													<button
														type="button"
														onClick={() => deleteDialogue.open(exp.execution.id)}
														className="text-red-600 font-semibold hover:text-red-900"
													>
														Delete
													</button>
												)}
												{canDelete(exp.execution.state) && canDownload(exp.execution.state) && <span className="text-gray-300">|</span>}
												{canDownload(exp.execution.state) && (
													<button
														type="button"
														onClick={() => MarketStateExportUtilities.handleDownload(exp)}
														className="text-blue-600 font-semibold hover:text-blue-900"
													>
														Download
													</button>
												)}
											</div>
										</td>
									</tr>
								))}
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>
			<Dialogue ref={deleteDialogue.ref} title="Delete Confirmation" onConfirm={() => deleteMutation.mutate(deleteDialogue.getValue()!)}>
				Are you sure you want to delete this export execution?
			</Dialogue>
		</Wrapper>
	);
};

const Wrapper: React.FC<React.PropsWithChildren<{}>> = ({ children }) => (
	<main>
		<div className="mx-auto max-w-7xl py-6 px-4 sm:px-6 lg:px-8">{children}</div>
	</main>
);

export default MarketStateExportPage;
